<div class="relative flex h-full w-full" role="dialog" aria-modal="true"
    aria-labelledby="review-application-title">

    <div class="absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-zinc-200/40 p-4 backdrop-blur-md sm:p-6 dark:bg-zinc-900/60"
        (click)="onBackdropClick($event)">

        <div class="review-application-panel primary-card-container-no-padding relative flex max-h-[92vh] w-full max-w-[820px] flex-col overflow-hidden"
            cdkTrapFocus [cdkTrapFocusAutoCapture]="true">

            <!-- ── Header ─────────────────────────────────────────────────── -->
            <div
                class="flex shrink-0 flex-row items-start gap-x-3 border-b border-border px-5 py-5 sm:px-6">

                @if (creator()) {
                <button type="button" (click)="viewCreatorProfile()"
                    class="size-11 shrink-0 cursor-pointer overflow-hidden rounded-full border border-border focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    [attr.aria-label]="'View profile for ' + creatorName()">
                    <atom-ldg-img class="pointer-events-none size-11"
                        [src]="imageService.getImageSource(creator()!.photoURL, 'gallery')"
                        [fallbackText]="creatorInitials()"
                        inputClass="flex-none" h="11" w="11" radius="full"></atom-ldg-img>
                </button>
                }

                <div class="min-w-0 flex-1">
                    <h2 id="review-application-title"
                        class="font-heading m-0 text-2xl leading-none font-extrabold tracking-tight text-primary sm:text-[26px]">
                        Review application
                    </h2>

                    <p class="m-0 mt-1.5 max-w-[58ch] text-[13px] text-secondary">
                        @if (pieceHire()) {
                        <span class="font-semibold text-primary">{{ creatorName() }}</span>
                        offered {{ networkCountLabelFor(putForwardChannels().length) }}.
                        Accept the channels you want delivered. The total is reserved now,
                        assuming every placement you leave on is delivered.
                        } @else if (description().hasPutForward) {
                        <span class="font-semibold text-primary">{{ description().creatorName }}</span>
                        applied to post on
                        <span class="font-semibold text-primary">{{ description().primaryLabel }}</span>
                        and put forward {{ networkCountLabelFor(description().putForwardCount) }} for
                        cross-posting. Un-select anything you don't want them to post on — the hire cost
                        updates as you go.
                        @if (description().tier) {
                        Rates are set by tier:
                        <span class="font-semibold text-primary">{{ description().tier }}</span>.
                        }
                        } @else {
                        <span class="font-semibold text-primary">{{ description().creatorName }}</span>
                        applied to post on
                        <span class="font-semibold text-primary">{{ description().primaryLabel }}</span>
                        only — no additional networks were put forward.
                        }
                    </p>
                </div>

                <button type="button" (click)="cancel()" aria-label="Close"
                    class="ml-auto grid size-8 shrink-0 cursor-pointer place-items-center rounded-full bg-muted text-secondary hover:bg-zinc-300 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary dark:hover:bg-zinc-600">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="size-4" aria-hidden="true">
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                    </svg>
                </button>
            </div>

            <!-- ── Body ───────────────────────────────────────────────────── -->
            <div class="scrollbar-transparent flex-1 overflow-y-auto px-5 py-5 sm:px-6">

                <!-- Primary network — never a choice, but the floor of the price has to be legible. -->
                @if (primaryRow(); as row) {
                <p class="m-0 mb-2.5 text-[11px] font-bold tracking-[0.08em] text-muted uppercase">
                    Primary network — always included
                </p>

                <div [class]="rowClassFor(row)" aria-disabled="true"
                    [attr.aria-label]="row.label + ' — required, always included'">
                    <span [class]="checkboxClassFor(row)" aria-hidden="true">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                            fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                            stroke-linejoin="round" class="size-3">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                    </span>

                    <span
                        class="font-heading grid size-[30px] shrink-0 place-items-center rounded-md text-[11.5px] font-extrabold text-white"
                        [style.background-color]="row.brandColour" aria-hidden="true">{{ row.mark }}</span>

                    <span class="min-w-0 flex-1">
                        <span class="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span class="text-[14.5px] font-semibold text-primary">{{ row.label }}</span>
                            <span
                                class="rounded-full bg-black px-1.5 py-0.5 text-xxs font-extrabold tracking-[0.06em] text-white uppercase">
                                Required
                            </span>
                        </span>
                        <span class="mt-0.5 block text-[11.5px] text-muted">{{ metricLine(row) }}</span>
                    </span>

                    <span class="font-heading shrink-0 text-[15px] font-bold text-primary">
                        @if (row.rate === null) {
                        <span class="text-muted" title="No follower count, so no rate to quote">—</span>
                        } @else {
                        {{ row.rate | currency: currencyCode() }}
                        }
                    </span>
                </div>
                }

                <!-- Put forward by the creator — the whole decision lives here. -->
                @if (putForwardRows().length > 0) {
                <p class="m-0 mt-5 mb-2.5 text-[11px] font-bold tracking-[0.08em] text-muted uppercase">
                    Put forward by the creator — {{ networkCountLabelFor(putForwardRows().length) }}
                </p>

                <div class="flex flex-col gap-[7px]">
                    @for (row of putForwardRows(); track row.socialChannel) {
                    <label [class]="rowClassFor(row)">
                        <input type="checkbox" class="sr-only" [checked]="row.included"
                            (change)="toggle(row.socialChannel)"
                            [attr.aria-label]="pieceHire() ? 'Include ' + row.label : 'Include ' + row.label + ' cross-post'">

                        <span [class]="checkboxClassFor(row)" aria-hidden="true">
                            @if (row.included) {
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"
                                stroke-linejoin="round" class="size-3">
                                <path d="M20 6 9 17l-5-5" />
                            </svg>
                            }
                        </span>

                        <span
                            class="font-heading grid size-[30px] shrink-0 place-items-center rounded-md text-[11.5px] font-extrabold text-white"
                            [style.background-color]="row.brandColour" aria-hidden="true">{{ row.mark }}</span>

                        <span class="min-w-0 flex-1">
                            <span class="block text-[14.5px] font-semibold text-primary">{{ row.label }}</span>
                            <span class="mt-0.5 block text-[11.5px] text-muted">{{ metricLine(row) }}</span>
                        </span>

                        <span class="font-heading shrink-0 text-[15px] font-bold text-primary">
                            @if (row.rate === null) {
                            <span class="text-muted" title="No follower count, so no rate to quote">—</span>
                            } @else {
                            {{ row.rate | currency: currencyCode() }}
                            }
                        </span>
                    </label>
                    }
                </div>
                }

                <!-- What was left on the table. Informational — the brand cannot buy these here. -->
                @if (notPutForward().length > 0) {
                <div
                    class="mt-4 flex flex-row items-start gap-x-2 rounded-md border border-amber-600/30 bg-amber-500/[0.08] px-3.5 py-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="mt-px size-4 shrink-0 text-warning" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                    </svg>
                    <p class="m-0 text-[12.5px] text-secondary">
                        <span class="font-semibold text-primary">Not put forward:</span>
                        {{ notPutForwardText() }}.
                        @if (notPutForwardHasNoAccount()) {
                        You can request these later if the creator links an account.
                        } @else if (pieceHire()) {
                        They stay off this hire.
                        } @else {
                        You can request these later as a cross-post.
                        }
                    </p>
                </div>
                }

                <!-- Pricing is banded by audience, so a missing follower count is a missing rate. -->
                @if (hasPendingPricing()) {
                <div
                    class="mt-4 flex flex-row items-start gap-x-2 rounded-md border border-amber-600/30 bg-amber-500/[0.08] px-3.5 py-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        class="mt-px size-4 shrink-0 text-warning" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 16v-4" />
                        <path d="M12 8h.01" />
                    </svg>
                    <p class="m-0 text-[12.5px] text-secondary">
                        <span class="font-semibold text-primary">Audience bonus pending:</span>
                        @if (followersLoading()) {
                        we're still checking {{ creatorName() }}'s follower counts.
                        } @else {
                        we don't have follower counts for every network here, and the bonus is set by
                        audience size. The figures below exclude it, so the real cost will be higher.
                        }
                        You can still hire — the final amount is calculated when you do.
                    </p>
                </div>
                }

                @if (pieceQuoteError(); as error) {
                <p class="m-0 mt-4 text-[12.5px] text-red-600 dark:text-red-400" role="alert">{{ error }}</p>
                }

                @if (piecePriceChanged()) {
                <p class="m-0 mt-4 rounded-md border border-amber-600/30 bg-amber-500/[0.08] px-3.5 py-3 text-[12.5px] text-secondary"
                    role="status">
                    <span class="font-semibold text-primary">The price changed since it was quoted.</span>
                    Check the new total below and hire again to confirm.
                </p>
                }

                <!-- Cost breakdown — the answer to "how does the cost change". -->
                <div [class]="pieceQuoteStale() ? 'mt-5 overflow-hidden rounded-lg border border-border opacity-60 transition-opacity' : 'mt-5 overflow-hidden rounded-lg border border-border transition-opacity'"
                    [attr.aria-busy]="pieceQuoteStale()">

                    @if (pieceHire()) {
                    <div [class]="breakdownRowClass(true)">
                        <span [class]="breakdownLabelClass(true)">Deliverables</span>
                        <span [class]="breakdownFigureClass(true)">
                            {{ breakdown().primaryNet | currency: currencyCode() }}
                        </span>
                    </div>
                    } @else if (primaryRow(); as row) {
                    <div [class]="breakdownRowClass(true)">
                        <span [class]="breakdownLabelClass(true)">{{ row.label }} primary post</span>
                        <span [class]="breakdownFigureClass(true)">
                            {{ breakdown().primaryNet | currency: currencyCode() }}
                        </span>
                    </div>
                    }

                    @if (primaryBonusPending()) {
                    <div [class]="breakdownRowClass(true)">
                        <span [class]="breakdownLabelClass(true)">Audience bonus</span>
                        <span [class]="breakdownFigureClass(true)">Pending</span>
                    </div>
                    }

                    @for (row of putForwardRows(); track row.socialChannel) {
                    <div [class]="breakdownRowClass(row.included)">
                        <span [class]="breakdownLabelClass(row.included)">{{ row.label }}{{ pieceHire() ? '' : ' cross-post' }}</span>
                        <span [class]="breakdownFigureClass(row.included)">
                            @if (row.rate === null) {
                            Pending
                            } @else {
                            {{ row.rate | currency: currencyCode() }}
                            }
                        </span>
                    </div>
                    }

                    @if (pieceQuote(); as quote) {
                    @if (quote.partnerContribution > 0) {
                    <div [class]="breakdownRowClass(true)">
                        <span [class]="breakdownLabelClass(true)">Partner contribution</span>
                        <span [class]="breakdownFigureClass(true)">
                            −{{ quote.partnerContribution / 100 | currency: currencyCode() }}
                        </span>
                    </div>
                    }
                    }

                    @if (breakdown().vat > 0) {
                    <div [class]="breakdownRowClass(true)">
                        <span [class]="breakdownLabelClass(true)">VAT (20%)</span>
                        <span [class]="breakdownFigureClass(true)">
                            {{ breakdown().vat | currency: currencyCode() }}
                        </span>
                    </div>
                    }

                    <div class="flex flex-row items-start justify-between gap-4 bg-accent px-4 py-3.5"
                        aria-live="polite">
                        <span class="mt-1 flex-1 text-xs font-bold tracking-[0.06em] text-accent uppercase">
                            @if (hasPendingPricing()) {
                            Total to hire {{ creatorName() }} — from
                            } @else {
                            Total to hire {{ creatorName() }}
                            }
                        </span>
                        <span class="flex flex-col items-end text-right">
                            <span
                                class="font-heading text-2xl leading-none font-extrabold tracking-tight text-primary sm:text-[26px]">
                                {{ breakdown().total | currency: currencyCode() }}
                            </span>
                            <span class="mt-1.5 text-[11.5px] font-semibold text-secondary">
                                @if (pieceHire() && !pieceQuote()) {
                                Pricing this hire…
                                } @else if (pieceHire()) {
                                Reserved now, exactly as quoted
                                } @else if (subLine().kind === 'primary-only') {
                                Primary post only
                                } @else {
                                {{ breakdown().primaryGross | currency: currencyCode() }} primary +
                                {{ breakdown().crossGross | currency: currencyCode() }} across
                                {{ networkCountLabelFor(includedCrossCount()) }}
                                }
                            </span>
                        </span>
                    </div>
                </div>
            </div>

            <!-- ── Footer ─────────────────────────────────────────────────── -->
            <div
                class="flex shrink-0 flex-row flex-wrap items-center gap-4 border-t border-border bg-muted px-5 py-4 sm:px-6">

                <div class="flex flex-col">
                    <span class="text-[11.5px] text-muted">
                        @if (hasPendingPricing()) { Hire cost from } @else { Hire cost }
                    </span>
                    <span
                        class="font-heading text-2xl font-extrabold tracking-tight text-primary">
                        {{ breakdown().total | currency: currencyCode() }}
                    </span>
                </div>

                @if (walletAfter() !== null) {
                <div class="flex flex-col">
                    <span class="text-[11.5px] text-muted">Wallet balance after</span>
                    <span [class]="walletFigureClass()">
                        {{ walletAfter() | currency: walletCurrencyCode() }}
                    </span>
                </div>
                }

                <div class="ml-auto flex flex-row flex-wrap items-center justify-end gap-x-2.5 gap-y-1">
                    @if (pieceHireAction() === 'nothing-owed') {
                    <p class="m-0 w-full text-right text-[12px] font-semibold text-warning" role="status">
                        {{ NOTHING_OWED_COPY }}
                    </p>
                    }
                    <button type="button" (click)="cancel()"
                        class="secondary-button inline-flex flex-row items-center text-primary">
                        Cancel
                    </button>

                    <button type="button" (click)="hire()" [disabled]="isHiring() || hireBlocked()"
                        class="primary-button inline-flex flex-row items-center gap-x-2 disabled:cursor-not-allowed disabled:opacity-60">
                        @if (isHiring()) {
                        <svg aria-hidden="true" class="size-4 animate-spin fill-white text-white/30"
                            viewBox="0 0 100 101" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                fill="currentColor" />
                            <path
                                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                fill="currentFill" />
                        </svg>
                        Hiring…
                        } @else if (pieceHire() && pieceHireAction() === null) {
                        Pricing…
                        } @else if (pieceHireAction() === 'nothing-owed') {
                        Nothing to hire
                        } @else if (pieceHireAction() === 'delivery-only') {
                        Hire for UGC delivery · {{ breakdown().total | currency: currencyCode() }}
                        } @else {
                        Hire on {{ networkCountText() }} · {{ breakdown().total | currency: currencyCode() }}
                        }
                    </button>
                </div>
            </div>
        </div>
    </div>
</div>
